import axios from 'axios';
// Base URL for your Django backend.
// On every cloverridgetech.com host we call the API same-origin (/api, proxied
// by nginx to gunicorn). Relative is the only URL that works from all of them:
//
//   - CSP. nginx sends `connect-src 'self' ...`, and 'self' is the *page's*
//     origin. From www, an absolute https://cloverridgetech.com/api is a
//     different origin than 'self' and is not otherwise listed, so the browser
//     blocks the request before it is sent. Apex worked only because there
//     'self' happened to match.
//   - CORS. Same reason, second gate: from www the absolute apex URL is
//     cross-origin, and these requests carry cookies (withCredentials).
//
// Same-origin clears both, and keeps cookies/CSRF first-party. Non-production
// hosts (localhost) still use the build env.
const resolveBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'cloverridgetech.com' || host.endsWith('.cloverridgetech.com')) {
      return '/api';
    }
  }
  return process.env.REACT_APP_API_BASE_URL || 'https://cloverridgetech.com/api';
};
const BASE_URL = resolveBaseUrl();
// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for CSRF
});
// Request interceptor for logging (development only)
apiClient.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);
// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
    // Handle common errors
    if (error.response?.status === 404) {
      console.error('Resource not found');
    } else if (error.response?.status >= 500) {
      console.error('Server error - please try again later');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - please check your connection');
    }
    return Promise.reject(error);
  }
);
// API service functions
export const apiService = {
  // Articles API
  articles: {
    getAll: () => apiClient.get('/articles/'),
    getById: (id) => apiClient.get(`/articles/${id}/`),
    getByTag: (tag) => apiClient.get(`/articles/?tags=${tag}`),
  },
  // Team API
  team: {
    getAll: () => apiClient.get('/team/'),
    getById: (id) => apiClient.get(`/team/${id}/`),
  },
  // Contact API
  contact: {
    submit: async (data) => {
      // First get CSRF token
      await apiClient.get('/csrf/');
      
      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      // Submit with CSRF token in header
      return apiClient.post('/contact/', data, {
        headers: {
          'X-CSRFToken': csrfToken,
        },
      });
    },
  },
  // Site Settings API
  settings: {
    get: () => apiClient.get('/settings/'),
  },
  // Scheduling API (scheduling.cloverridgetech.com booking page)
  scheduling: {
    getMeetingTypes: () => apiClient.get('/scheduling/meeting-types/'),
    getSlots: (meetingTypeId) =>
      apiClient.get('/scheduling/slots/', {
        params: meetingTypeId ? { meeting_type: meetingTypeId } : {},
      }),
    book: async (data) => {
      // Match the contact form CSRF flow.
      await apiClient.get('/csrf/');
      const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken='))
        ?.split('=')[1];
      return apiClient.post('/scheduling/book/', data, {
        headers: { 'X-CSRFToken': csrfToken },
      });
    },
  },
  // Contracts API (SOW/DOD signing page at /sign/<token>)
  contracts: {
    get: (token) => apiClient.get(`/contracts/sign/${token}/`),
    sign: async (token, data) => {
      // Match the contact form CSRF flow.
      await apiClient.get('/csrf/');
      const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken='))
        ?.split('=')[1];
      return apiClient.post(`/contracts/sign/${token}/`, data, {
        headers: { 'X-CSRFToken': csrfToken },
      });
    },
    decline: async (token, reason) => {
      await apiClient.get('/csrf/');
      const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken='))
        ?.split('=')[1];
      return apiClient.post(
        `/contracts/sign/${token}/decline/`,
        { reason },
        { headers: { 'X-CSRFToken': csrfToken } }
      );
    },
  },
  // Tags API (for articles)
  tags: {
    getAll: () => apiClient.get('/tags/'),
  },
  // Stats API
  stats: {
    get: () => apiClient.get('/stats/'),
  },
};
export const extractData = (response) => {
    const data = response.data;
    // Check if this is a paginated response from Django REST Framework
    if (data && typeof data === 'object' && data.hasOwnProperty('results')) {
      console.log('📄 Paginated response detected, extracting results');
      return data.results;
    }
    // Otherwise return the data directly
    return data;
  };
// Helper function for error handling
export const handleApiError = (error, fallbackMessage = 'Something went wrong') => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.response?.data?.detail) {
    return error.response.data.detail;
  } else if (error.message) {
    return error.message;
  }
  return fallbackMessage;
};
export default apiService;
